import { Check, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useTheme, themePresets, ThemePreset } from "@/hooks/useTheme";
import { cn } from "@/lib/utils";

export function ThemeCustomizer() {
  const { currentTheme, setTheme, customColors, setCustomColors } = useTheme();

  const presetEntries = Object.entries(themePresets).filter(
    ([key]) => key !== "custom"
  ) as [ThemePreset, typeof themePresets[ThemePreset]][];

  const customPreset = themePresets.custom;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9">
          <Palette size={18} />
          <span className="sr-only">Theme settings</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[320px] sm:w-[400px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Palette size={18} />
            Theme Customization
          </SheetTitle>
          <SheetDescription>
            Choose a color palette for the website
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6">
          <div className="grid grid-cols-1 gap-3">
            {presetEntries.map(([key, preset]) => (
              <button
                key={key}
                onClick={() => setTheme(key)}
                className={cn(
                  "relative flex items-center gap-4 p-4 rounded-lg border-2 transition-all hover:border-primary/50 text-left",
                  currentTheme === key
                    ? "border-primary bg-primary/5"
                    : "border-border bg-card hover:bg-muted/50"
                )}
              >
                {/* Color Preview */}
                <div className="flex items-center flex-shrink-0">
                  <div
                    className="w-10 h-10 rounded-full border-2 border-background shadow-sm"
                    style={{ backgroundColor: preset.preview.primary }}
                  />
                  <div
                    className="w-10 h-10 rounded-full border-2 border-background shadow-sm -ml-3"
                    style={{ backgroundColor: preset.preview.accent }}
                  />
                </div>

                {/* Name & Description */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{preset.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {preset.description}
                  </p>
                </div>

                {/* Selected Indicator */}
                {currentTheme === key && (
                  <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-primary-foreground" />
                  </div>
                )}
              </button>
            ))}

            {/* Custom Theme Option */}
            <button
              onClick={() => setTheme("custom")}
              className={cn(
                "relative flex items-center gap-4 p-4 rounded-lg border-2 transition-all hover:border-primary/50 text-left",
                currentTheme === "custom"
                  ? "border-primary bg-primary/5"
                  : "border-border bg-card hover:bg-muted/50"
              )}
            >
              {/* Color Preview */}
              <div className="flex items-center flex-shrink-0">
                <div
                  className="w-10 h-10 rounded-full border-2 border-background shadow-sm"
                  style={{ backgroundColor: customColors.primary }}
                />
                <div
                  className="w-10 h-10 rounded-full border-2 border-background shadow-sm -ml-3"
                  style={{ backgroundColor: customColors.accent }}
                />
              </div>

              {/* Name & Description */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{customPreset.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {customPreset.description}
                </p>
              </div>

              {/* Selected Indicator */}
              {currentTheme === "custom" && (
                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-primary-foreground" />
                </div>
              )}
            </button>
          </div>

          {/* Custom Color Pickers */}
          {currentTheme === "custom" && (
            <div className="mt-4 p-4 rounded-lg border border-border bg-muted/30 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="primary-color" className="text-sm font-medium">
                  Primary Color
                </Label>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <input
                      type="color"
                      id="primary-color"
                      value={customColors.primary}
                      onChange={(e) =>
                        setCustomColors({ ...customColors, primary: e.target.value })
                      }
                      className="w-12 h-10 rounded-lg border border-border cursor-pointer"
                    />
                  </div>
                  <Input
                    value={customColors.primary}
                    onChange={(e) =>
                      setCustomColors({ ...customColors, primary: e.target.value })
                    }
                    placeholder="#000000"
                    className="flex-1 font-mono text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="accent-color" className="text-sm font-medium">
                  Accent Color
                </Label>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <input
                      type="color"
                      id="accent-color"
                      value={customColors.accent}
                      onChange={(e) =>
                        setCustomColors({ ...customColors, accent: e.target.value })
                      }
                      className="w-12 h-10 rounded-lg border border-border cursor-pointer"
                    />
                  </div>
                  <Input
                    value={customColors.accent}
                    onChange={(e) =>
                      setCustomColors({ ...customColors, accent: e.target.value })
                    }
                    placeholder="#ffffff"
                    className="flex-1 font-mono text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground mt-6 text-center">
            Theme changes are applied instantly and saved for future visits.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
